import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, Plus, ChevronLeft, ChevronRight, MapPin, User, Wrench, CheckCircle, X, Phone, Building2 } from 'lucide-react';

interface Booking {
  id: string;
  customerName: string;
  phone: string;
  address: string;
  service: string;
  date: string;
  time: string;
  technician: string;
  status: 'confirmed' | 'pending' | 'completed' | 'cancelled';
  notes?: string;
}

const services = [
  'CCTV Installation', 'CCTV Maintenance', 'Alarm System Installation',
  'Alarm System Maintenance', 'Network Setup', 'Starlink Installation',
  'Smart Home Setup', 'Site Survey', 'Emergency Repair', 'Annual Service',
];

const technicians = ['Kamal Perera', 'Nimal Silva', 'Suresh Kumar', 'Dilshan Fernando'];
const timeSlots = ['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'];

const statusColors: Record<string, string> = {
  confirmed: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const mockBookings: Booking[] = [
  { id: 'BK001', customerName: 'Rajesh Gunawardena', phone: '+94711234567', address: '45 Galle Road, Colombo 3', service: 'CCTV Installation', date: '2026-07-10', time: '9:00 AM', technician: 'Kamal Perera', status: 'confirmed' },
  { id: 'BK002', customerName: 'Priya Chandrasekara', phone: '+94729876543', address: 'Hotel Reef, Negombo', service: 'Network Setup', date: '2026-07-10', time: '11:00 AM', technician: 'Nimal Silva', status: 'confirmed' },
  { id: 'BK003', customerName: 'MAS Holdings', phone: '+94112345678', address: 'Katunayake Industrial Zone', service: 'Alarm System Maintenance', date: '2026-07-11', time: '10:00 AM', technician: 'Suresh Kumar', status: 'pending' },
  { id: 'BK004', customerName: 'Lanka Hospitals', phone: '+94115678901', address: 'Colombo 5', service: 'Site Survey', date: '2026-07-11', time: '2:00 PM', technician: 'Dilshan Fernando', status: 'pending' },
  { id: 'BK005', customerName: 'Dialog Axiata', phone: '+94112234567', address: 'Maradana, Colombo', service: 'Starlink Installation', date: '2026-07-12', time: '8:00 AM', technician: 'Kamal Perera', status: 'confirmed' },
];

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (number | null)[] = Array(firstDay).fill(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);
  return days;
}

export default function BookingPage() {
  const [bookings, setBookings] = useState<Booking[]>(mockBookings);
  const [showForm, setShowForm] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date(2026, 6));
  const [selectedDay, setSelectedDay] = useState<number | null>(10);
  const [form, setForm] = useState({ customerName: '', phone: '', address: '', service: '', date: '', time: '', notes: '' });

  const days = getCalendarDays(currentDate.getFullYear(), currentDate.getMonth());
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const todayBookings = bookings.filter(b => {
    if (!selectedDay) return false;
    const d = new Date(b.date);
    return d.getDate() === selectedDay && d.getMonth() === currentDate.getMonth();
  });

  const bookedDays = bookings.map(b => {
    const d = new Date(b.date);
    return d.getMonth() === currentDate.getMonth() ? d.getDate() : null;
  }).filter(Boolean) as number[];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const tech = technicians[Math.floor(Math.random() * technicians.length)];
    const newBooking: Booking = {
      id: `BK${String(bookings.length + 1).padStart(3, '0')}`,
      customerName: form.customerName,
      phone: form.phone,
      address: form.address,
      service: form.service,
      date: form.date,
      time: form.time,
      technician: tech,
      status: 'confirmed',
      notes: form.notes,
    };
    setBookings(prev => [...prev, newBooking]);
    setForm({ customerName: '', phone: '', address: '', service: '', date: '', time: '', notes: '' });
    setShowForm(false);
  };

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA] p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-200">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Booking System</h1>
            <p className="text-xs text-gray-500">Site visits · Installations · Maintenance · Scheduled jobs</p>
          </div>
        </div>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-medium px-4 py-2.5 rounded-xl hover:shadow-lg hover:shadow-teal-200 transition-all">
          <Plus className="w-4 h-4" /> New Booking
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <span className="text-sm font-semibold text-gray-900">{monthName}</span>
            <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map(d => <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, i) => (
              <button
                key={i}
                onClick={() => day && setSelectedDay(day)}
                disabled={!day}
                className={`aspect-square text-xs rounded-lg flex items-center justify-center transition-all relative ${
                  !day ? '' :
                  selectedDay === day ? 'bg-teal-500 text-white font-semibold' :
                  bookedDays.includes(day) ? 'bg-teal-50 text-teal-700 font-medium hover:bg-teal-100' :
                  'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {day}
                {day && bookedDays.includes(day) && selectedDay !== day && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 bg-teal-400 rounded-full" />
                )}
              </button>
            ))}
          </div>

          {/* Today's stats */}
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
            {['confirmed', 'pending'].map(status => {
              const count = bookings.filter(b => {
                const d = new Date(b.date);
                return d.getDate() === selectedDay && d.getMonth() === currentDate.getMonth() && b.status === status;
              }).length;
              return (
                <div key={status} className="flex items-center justify-between text-xs">
                  <span className="capitalize text-gray-500">{status}</span>
                  <span className={`font-semibold px-2 py-0.5 rounded-full ${statusColors[status]}`}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bookings for selected day */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">
              {selectedDay ? `Bookings for July ${selectedDay}, 2026` : 'Select a date'}
            </h2>
            <span className="text-xs text-gray-500">{todayBookings.length} booking{todayBookings.length !== 1 ? 's' : ''}</span>
          </div>
          {todayBookings.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No bookings for this day</p>
            </div>
          ) : (
            todayBookings.map(booking => (
              <motion.div key={booking.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-400">{booking.id}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[booking.status]}`}>
                        {booking.status}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 mt-0.5">{booking.customerName}</h3>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-1 rounded-lg">{booking.time}</span>
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Wrench className="w-3 h-3" /> {booking.service}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <MapPin className="w-3 h-3" /> {booking.address}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <User className="w-3 h-3" /> {booking.technician}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Phone className="w-3 h-3" /> {booking.phone}
                  </div>
                </div>
              </motion.div>
            ))
          )}

          {/* All upcoming */}
          <div className="mt-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">All Upcoming</h3>
            <div className="space-y-2">
              {bookings.filter(b => b.status !== 'completed' && b.status !== 'cancelled').slice(0, 5).map(b => (
                <div key={b.id} className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 px-4 py-2.5 hover:shadow-sm transition-shadow">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600">
                    {new Date(b.date).getDate()}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-gray-900">{b.customerName}</p>
                    <p className="text-[11px] text-gray-400">{b.service} · {b.time}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[b.status]}`}>{b.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* New Booking Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold text-gray-900">New Booking</h3>
                <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Customer Name</label>
                    <input required value={form.customerName} onChange={e => setForm({...form, customerName: e.target.value})} className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-200" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Phone</label>
                    <input required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-200" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Address</label>
                  <input required value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-200" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Service</label>
                  <select required value={form.service} onChange={e => setForm({...form, service: e.target.value})} className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-200 bg-white">
                    <option value="">Select service…</option>
                    {services.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Date</label>
                    <input required type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-200" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Time</label>
                    <select required value={form.time} onChange={e => setForm({...form, time: e.target.value})} className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-200 bg-white">
                      <option value="">Select time…</option>
                      {timeSlots.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Notes (optional)</label>
                  <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-200 resize-none" />
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
                  <button type="submit" className="flex-1 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-cyan-600 rounded-xl hover:shadow-lg transition-all">
                    Confirm Booking
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
